/**
 * ChatModule - AI Chat Interface Module (Fixed Double Replies & Line Breaks)
 * 
 * Manages the chat interface for AI strategic assistance
 * FIXED: Removed duplicate message handling and added proper line break rendering
 * 
 * @module modules/chat/ChatModule
 */

import AppState from '../../core/AppState.js';
import EventBus, { AppEvents } from '../../core/EventBus.js';
import Config from '../../core/Config.js';
import renderQueue from '../../core/RenderQueue.js';
import apiService from '../../services/ApiService.js';
import promptService from '../../services/PromptService.js';
import container from '../../core/ServiceContainer.js';
import { SERVICE_TOKENS as T } from '../../core/services.tokens.js';
import { PromptEditor } from '../../components/PromptEditor.js';
import * as ActionRegistry from '../../core/ActionRegistry.js';
import { parseDataJSON } from '../../utils/dataset.js';
import FeedbackService from '../../services/FeedbackService.js';
import panelResize from '../../utils/PanelResize.js';

export class ChatModule {
  constructor() {
    this.container = null;
    this.messages = [];
    this.conversationDepth = 0;
    this.isTyping = false;
    this.conversationContext = {
      hasSpecificGoal: false,
      discussedMetrics: [],
      mentionedTimeframe: false,
      actionableItems: []
    };
    // Removed isRendering flag - using RenderQueue instead
  }
  
  /**
   * Initialize the module
   */
  async initialize() {
    // Resolve services via DI (fallback to direct singletons)
    this.api = container.get(T.API) || apiService;
    this.prompts = container.get(T.PROMPTS) || promptService;
    // Initialize PromptService if needed
    if (!this.prompts.initialized) {
      await this.prompts.initialize();
    }
    
    // Do not load persisted chat history to keep sessions light
    try { if (import.meta.env?.DEV) console.log('💬 Chat history loading disabled'); } catch (_) {}
    
    // Set up event listeners (FIXED: removed non-existent handlers)
    this.setupEventListeners();
    // Register global click actions via ActionRouter
    this.registerRouterActions();
    
    // Subscribe to state changes - use RenderQueue for efficient rendering
    AppState.subscribe('chat.messages', (messages) => {
      if (messages && messages.length !== this.messages.length) {
        this.messages = [...messages]; // Create new array to avoid reference issues
        renderQueue.markDirty(this, 5); // Priority 5 for chat updates
      }
    });
  }
  
  /**
   * Set up event listeners (FIXED: removed calls to non-existent methods)
   */
  setupEventListeners() {
    // Listen for chat clear events only
    EventBus.on(AppEvents.CHAT_CLEARED, () => {
      this.clearChat();
    });
    
    // Removed: CHAT_MESSAGE_SENT and CHAT_MESSAGE_RECEIVED listeners
    // These were calling non-existent handleUserMessage/handleAIResponse methods
  }
  
  /**
   * Activate the module (when tab is shown)
   */
  activate() {
    console.log('💬 Chat module activated');
    renderQueue.markDirty(this, 10); // High priority for tab activation
  }
  
  /**
   * Mount the chat interface
   */
  mount(container) {
    this.container = container;
    this.render();
  }
  
  /**
   * Render the chat interface - called by RenderQueue
   */
  render() {
    if (!this.container) return;
    
    // Determine container class based on conversation state
    const containerClass = this.messages.length > 0 ? 
      (this.conversationDepth > 2 ? 'chat-container active-conversation' : 'chat-container has-content') : 
      'chat-container';
    
    this.container.innerHTML = `
      <div class="panel panel-layout chat-panel" data-theme="unified">
        <header class="panel__header">
          <h3 class="panel__title">My Objectives & Challenges</h3>
          <div class="panel__actions">
            <span class="conversation-depth">Depth: ${this.conversationDepth}</span>
            <button class="btn-ghost btn-small" data-action="clear-chat">New Conversation</button>
          </div>
        </header>
        <div class="panel__content" style="display:flex;flex-direction:column;min-height:0">
          <div class="${containerClass}" id="chat-container-main" style="flex:1;display:flex;flex-direction:column;min-height:0">
            <!-- Messages (with welcome when empty) -->
            <div class="chat-messages" id="chatMessages" style="flex:1;overflow:auto">
              ${this.renderMessages()}
            </div>
            <!-- Prompt at bottom of conversation -->
            <div class="chat-input-container sticky" style="position:sticky;bottom:0;z-index:1">
              ${this.isTyping ? this.renderTypingIndicator() : ''}
              <div class="chat-input-wrapper">
                <textarea 
                  id="chatInput"
                  class="chat-input"
                  placeholder="Ask about strategic initiatives..."
                  rows="2"
                ></textarea>
                <button class="btn-primary send-btn" data-action="send-message">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 10L8 4V8H14V12H8V16L2 10Z" transform="rotate(-90 10 10)"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Attach event handlers
    this.attachEventHandlers();
    
    // Use PanelResizeManager for consistent resizing
    const chatContainer = this.container.querySelector('.chat-container');
    if (chatContainer) {
      // ID already set in markup; ensure it exists as a fallback
      if (!chatContainer.id) chatContainer.id = 'chat-container-main';
      
      // Register with PanelResizeManager
      panelResize.register(chatContainer.id, {
        direction: 'vertical',
        min: 320,
        max: '85vh',
        persist: true,
        handle: 'bottom',
        onResize: () => this.scrollToBottom()
      });
    }
    
    // Scroll to bottom
    this.scrollToBottom();
  }
  
  /**
   * Render messages (FIXED: proper line break handling)
   */
  renderMessages() {
    if (this.messages.length === 0) {
      return this.renderWelcomeMessage();
    }
    
    const thumbs = (msg) => {
      // Gate by feature + user toggle
      const enable = (() => {
        try { return import.meta.env?.VITE_ENABLE_ANALYTICS === 'true' && localStorage.getItem('enableAnalytics') === 'true'; } catch(_) { return false; }
      })();
      if (!enable) return '';
      if (msg.sender !== 'ai') return '';
      return `
        <div class="msg-feedback" style="display:flex;gap:8px;margin-top:6px;opacity:.8">
          <button class="btn btn-small" title="Helpful" data-action="feedback-up" data-message-id="${msg.id}">Helpful</button>
          <button class="btn btn-small" title="Not helpful" data-action="feedback-down" data-message-id="${msg.id}">Not helpful</button>
          <button class="btn-link" data-action="feedback-rate" data-message-id="${msg.id}">Rate</button>
        </div>`;
    };

    return this.messages.map(msg => `
      <div class="message ${msg.sender}">
        <div class="message-avatar">
          ${msg.sender === 'user' ? 'You' : 'AI'}
        </div>
        <div class="message-content">
          ${this.formatMessageContent(msg.content)}
          ${msg.transfer ? this.renderTransferButton(msg.transfer) : ''}
          ${thumbs(msg)}
        </div>
      </div>
    `).join('');
  }
  
  /**
   * Format message content with proper line breaks (NEW)
   */
  formatMessageContent(content) {
    if (!content) return '';
    
    // Convert line breaks to HTML
    let formatted = content
      .replace(/\n\n/g, '</p><p>') // Double line breaks = paragraphs
      .replace(/\n/g, '<br>');     // Single line breaks = <br>
    
    // Wrap in paragraph if it contains paragraph breaks
    if (formatted.includes('</p><p>')) {
      formatted = `<p>${formatted}</p>`;
    }
    
    return formatted;
  }
  
  /**
   * Render welcome message
   */
  renderWelcomeMessage() {
    return `
      <div class="welcome-message">
        <h4>Welcome — what impact would you like to make?</h4>
        <p>Whose life would you like to change? Tell me in your own words and I’ll help turn it into a clear outcome with measurable results.</p>
        <div class="suggestions">
          <p>Examples:</p>
          <ul>
            <li>“Help our support team resolve issues faster for new customers.”</li>
            <li>“Increase renewals by making product setup effortless for admins.”</li>
            <li>“Reduce delivery lead times so operations can hit quarterly targets.”</li>
          </ul>
        </div>
      </div>
    `;
  }
  
  /**
   * Render transfer button for initiatives
   */
  renderTransferButton(initiative) {
    // Normalize key results for display
    const krs = Array.isArray(initiative.keyResults)
      ? initiative.keyResults.filter(Boolean)
      : (typeof initiative.keyResults === 'string' && initiative.keyResults.trim()
          ? initiative.keyResults.split(/\n|;\s*/).filter(Boolean)
          : []);
    // Normalize stakeholders for display
    const stakeholders = Array.isArray(initiative.stakeholders)
      ? initiative.stakeholders.filter(Boolean)
      : [];
    
    return `
      <div class="transfer-section">
        <div class="transfer-summary">
          <h4>Ready to Transfer</h4>
          <p><strong>${initiative.title || 'Strategic Initiative'}</strong></p>
          ${initiative.objective ? `<p class="objective-text">${initiative.objective}</p>` : ''}
          ${stakeholders.length ? `<p class="text-secondary" style="margin:.25rem 0 0">Stakeholders: ${stakeholders.slice(0,2).join(', ')}${stakeholders.length>2?` (+${stakeholders.length-2})`:''}</p>` : ''}
          ${krs.length ? `
            <div class="kr-block">
              <strong>Key Results:</strong>
              <ul class="kr-list">${krs.slice(0,5).map(kr => `<li>${kr}</li>`).join('')}</ul>
            </div>
          ` : ''}
        </div>
        <button class="btn-primary transfer-btn" data-action="transfer" data-initiative='${JSON.stringify(initiative).replace(/'/g, '&#39;')}'>
          Transfer to Define Initiative
        </button>
      </div>
    `;
  }
  
  /**
   * Render typing indicator
   */
  renderTypingIndicator() {
    return `
      <div class="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
    `;
  }
  
  /**
   * Attach event handlers to rendered elements
   */
  attachEventHandlers() {
    // Click actions are handled via ActionRouter mapping
    
    // Chat input
    const input = this.container.querySelector('#chatInput');
    if (input) {
      // Enter to send (without shift)
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.sendMessage();
        }
      });
      
      // Resizable for long chats
      input.style.resize = 'vertical';
      input.addEventListener('input', () => {
        input.style.height = 'auto';
        input.style.height = Math.min(input.scrollHeight, 300) + 'px';
      });
    }
    
    // Transfer buttons handled via ActionRouter
  }

  registerRouterActions() {
    const mapping = {
      'send-message': ({}) => this.sendMessage(),
      'clear-chat': ({}) => this.clearChat(),
      'transfer': ({ dataset }) => {
        try {
          const initiative = parseDataJSON(dataset?.initiative, null);
          if (initiative) this.transferInitiative(initiative);
        } catch (error) {
          console.error('Failed to parse initiative:', error);
        }
      },
      'feedback-up': async ({ target }) => {
        try {
          await FeedbackService.submitMessageFeedback({ messageId: target?.dataset?.messageId, role: 'assistant', tabId: 'objectives', vote: 'up' });
          try { EventBus.emit(AppEvents.TOAST_SHOW, { message: 'Thanks for the feedback!', type: 'success' }); } catch(_) {}
          this.showToast('Thanks for the feedback!', 'success');
        } catch(_) { this.showToast('Could not record feedback', 'error'); }
      },
      'feedback-down': async ({ target }) => {
        try {
          await FeedbackService.submitMessageFeedback({ messageId: target?.dataset?.messageId, role: 'assistant', tabId: 'objectives', vote: 'down' });
          try { EventBus.emit(AppEvents.TOAST_SHOW, { message: 'Thanks for the feedback!', type: 'success' }); } catch(_) {}
          this.showToast('Thanks for the feedback!', 'success');
        } catch(_) { this.showToast('Could not record feedback', 'error'); }
      },
      'feedback-rate': async ({ target }) => {
        const id = target?.dataset?.messageId;
        const content = `
          <div style="display:flex;flex-direction:column;gap:10px">
            <label>Rating
              <select id="rate-score" class="form-control">
                <option value="5">5 - Excellent</option>
                <option value="4">4 - Good</option>
                <option value="3">3 - Fair</option>
                <option value="2">2 - Poor</option>
                <option value="1">1 - Bad</option>
              </select>
            </label>
            <label>Comment (optional)
              <textarea id="rate-comment" rows="3" class="form-control" placeholder="What made this good/bad?"></textarea>
            </label>
          </div>`;
        const { ModalService } = await import('../../ui-kit/ModalService.jsx');
        const close = ModalService.open({
          title: 'Rate Reply',
          content,
          actions: [
            { text: 'Cancel', class: 'uk-btn uk-btn-secondary', action: () => {} },
            { text: 'Submit', class: 'uk-btn uk-btn-primary', action: async () => {
              const rating = parseInt(document.getElementById('rate-score')?.value || '0', 10) || null;
              const comment = document.getElementById('rate-comment')?.value?.trim() || '';
              try {
                await FeedbackService.submitMessageFeedback({ messageId: id, role: 'assistant', tabId: 'objectives', vote: 'none', rating, comment });
                try { close?.(); } catch(_) {}
                try { EventBus.emit(AppEvents.TOAST_SHOW, { message: 'Rating saved. Thank you!', type: 'success' }); } catch(_) {}
                this.showToast('Rating saved. Thank you!', 'success');
              } catch (_) { this.showToast('Could not save rating', 'error'); }
            }}
          ]
        });
      }
    };
    // Always register via central ActionRegistry; App will install maps once
    ActionRegistry.registerMap('chat', mapping);
  }
  
  /**
   * Send message
   */
  async sendMessage() {
    const input = this.container.querySelector('#chatInput');
    const message = input?.value.trim();
    
    if (!message) return;
    
    // Clear input
    input.value = '';
    input.style.height = 'auto';
    
    // Update conversation context
    this.updateConversationContext(message);
    
    // Add user message (FIXED: only add to AppState, not both)
    this.addMessage(message, 'user');
    
    // Get AI response
    await this.getAIResponse(message);
  }
  
  /**
   * Update conversation context based on user input
   */
  updateConversationContext(message) {
    const lowerMessage = message.toLowerCase();
    
    // Check for specific goal indicators
    if (lowerMessage.includes('improve') || lowerMessage.includes('reduce') || 
        lowerMessage.includes('increase') || lowerMessage.includes('accelerate')) {
      this.conversationContext.hasSpecificGoal = true;
    }
    
    // Check for metrics
    const metricPattern = /\d+%|\$[\d,]+|[\d,]+\s*(units|customers|days|weeks|months)/gi;
    const metrics = message.match(metricPattern);
    if (metrics) {
      this.conversationContext.discussedMetrics.push(...metrics);
    }
    
    // Check for timeframe
    if (lowerMessage.includes('by') || lowerMessage.includes('within') || 
        lowerMessage.includes('quarter') || lowerMessage.includes('year')) {
      this.conversationContext.mentionedTimeframe = true;
    }
  }
  
  /**
   * Get AI response with improved transfer detection
   */
  async getAIResponse(message) {
    this.setTyping(true);

    try {
      // Get conversation history for context
      const history = this.messages.slice(-6).map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));
      
      // Decide path (two-stage metrics flow behind feature flag)
      const metricsEnabled = (Config?.FEATURES?.METRICS_CHAT === true);
      const metricsLikely = metricsEnabled && /\b(status|progress|metric|metrics|how many|summary|health)\b/i.test(message);

      let promptKey = 'outcomeAssistant';
      let messages;
      let apiConfig;

      if (metricsLikely) {
        try {
          // Stage 1: intent extraction
          const intentMsg = (this.prompts || promptService).buildMessages(message, 'metricsIntent', history);
          const intentCfg = (this.prompts || promptService).getApiConfig('metricsIntent');
          const intentRes = await (this.api || apiService).call(intentMsg[intentMsg.length - 1].content, {
            systemRole: intentMsg[0].content,
            history: intentMsg.slice(1, -1),
            temperature: intentCfg.temperature,
            maxTokens: intentCfg.maxTokens,
            promptKey: intentCfg.promptKey,
            promptVersion: intentCfg.promptVersion
          });
          const raw = String(intentRes?.content || '').trim();
          const jsonText = raw.replace(/^```json\n?|```$/g, '').replace(/```/g, '');
          let intent;
          try { intent = JSON.parse(jsonText); } catch(_) { intent = { scope: 'portfolio' }; }

          // Stage 2: local compute
          const { computeFromIntent } = await import('../../services/MetricsQueryEngine.js');
          const results = await computeFromIntent(intent);

          // Stage 3: narrative via metricsAssistant using CONTEXT
          const context = { intent, results };
          const userInput = `${message}\n\nCONTEXT (JSON):\n\n\`\`\`json\n${JSON.stringify(context)}\n\`\`\``;
          promptKey = 'metricsAssistant';
          messages = (this.prompts || promptService).buildMessages(userInput, promptKey, history);
          apiConfig = (this.prompts || promptService).getApiConfig(promptKey);
        } catch (e) {
          // Fallback to single-call outcome assistant
          promptKey = 'outcomeAssistant';
          messages = (this.prompts || promptService).buildMessages(message, promptKey, history);
          apiConfig = (this.prompts || promptService).getApiConfig(promptKey);
        }
      } else {
        // Non-metrics: go with outcome assistant
        promptKey = 'outcomeAssistant';
        messages = (this.prompts || promptService).buildMessages(message, promptKey, history);
        apiConfig = (this.prompts || promptService).getApiConfig(promptKey);
      }
      
      console.log(`🤖 Using prompt: ${apiConfig.promptName} (depth: ${this.conversationDepth})`);
      
      // Analytics: prompt usage
      try { (await import('../../services/AnalyticsService.js'))?.default?.logPromptSent?.(apiConfig.promptKey || 'outcomeAssistant', { tabId: 'objectives' }); } catch(_) {}

      // FIRST API CALL: Get conversational response
      const response = await (this.api || apiService).call(messages[messages.length - 1].content, {
        systemRole: messages[0].content,
        history: messages.slice(1, -1),
        temperature: apiConfig.temperature,
        maxTokens: apiConfig.maxTokens,
        // 🆕 ADD PROMPT METADATA:
        promptKey: apiConfig.promptKey,
        promptVersion: apiConfig.promptVersion
      });
      
      // Clean response
      const cleanContent = response.content
        .replace(/\[TRANSFERABLE_INITIATIVE][\s\S]*?\[\/TRANSFERABLE_INITIATIVE]/g, '')
        .replace(/```json[\s\S]*?```/g, '')
        .trim();
      
      // ALWAYS try to generate transfer after EVERY message
      let transfer = null;
      console.log(`🎯 Attempting transfer generation (depth: ${this.conversationDepth})...`);
      
      // Generate transfer even on first message if it mentions goals
      transfer = await this.generateTransferableInitiative(message, cleanContent, history);
      
      if (transfer) {
        console.log('✅ Transfer ready:', transfer);
      } else {
        console.log('ℹ️ No clear initiative yet, keep chatting');
      }
      
      // Add AI response to chat with transfer if available (FIXED: single add)
      this.addMessage(cleanContent, 'ai', transfer);
      
    } catch (error) {
      console.error('AI response error:', error);
      
      // Provide helpful error messages
      let errorMessage = 'Sorry, I encountered an error. ';
      
      if (error.code === 'INVALID_API_KEY') {
        errorMessage = 'Please add your OpenAI API key in Settings to use the AI assistant.';
      } else if (error.code === 'RATE_LIMIT') {
        errorMessage = 'Rate limit reached. Please wait a moment and try again.';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your internet connection.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      this.addMessage(errorMessage, 'ai');
    } finally {
      this.setTyping(false);
    }
  }
  
  /**
   * Generate a transferable initiative with structured JSON response
   */
  async generateTransferableInitiative(userMessage, aiResponse, history) {
    try {
      // Build conversation summary
      const conversationSummary = this.buildConversationSummary(userMessage, aiResponse, history);
      
      // Define expected schema for structured response
      const INITIATIVE_SCHEMA = {
        hasInitiative: 'boolean',
        confidence: 'number',
        initiative: {
          title: 'string',
          description: 'string',
          objective: 'string',
          keyResults: 'array',
          strategicAlignment: 'string',
          priority: 'string',
          owner: 'string',
          status: 'string',
          // Impact Mapping terminology
          stakeholders: 'array'
        }
      };
      
      // Create extraction prompt with JSON mode
      const extractPrompt = `You are an expert at extracting structured initiatives from strategic conversations.

Analyze this conversation and extract any clear, actionable strategic initiative.

Conversation:
${conversationSummary}

Context indicators:
- Has specific goal: ${this.conversationContext.hasSpecificGoal}
- Discussed metrics: ${this.conversationContext.discussedMetrics.join(', ') || 'none'}
- Mentioned timeframe: ${this.conversationContext.mentionedTimeframe}

IMPORTANT INSTRUCTIONS:
1. You MUST respond with ONLY a valid JSON object
2. Do NOT include any text before or after the JSON
3. Do NOT use markdown code blocks
4. The JSON must be parseable by JSON.parse()

Extract ANY potential initiative if there's mention of goals, objectives, improvements, problems, metrics, or targets.

Required JSON structure:
{
  "hasInitiative": true or false,
  "confidence": 0.0 to 1.0,
  "initiative": {
    "title": "Clear outcome-focused title",
    "description": "What needs to be done",
    "objective": "The business outcome",
    "keyResults": ["Measurable result 1", "Measurable result 2"],
    "strategicAlignment": "Operational Excellence",
    "priority": "medium",
    "owner": "TBD",
    "status": "draft",
    "stakeholders": ["Primary stakeholder / end-user", "Other stakeholder"]
  }
}`;
      
      // Call API for extraction with structured response
      const extractResponse = await (this.api || apiService).call(extractPrompt, {
        temperature: 0.1,
        maxTokens: 500,
        promptKey: 'initiativeTransfer',
        promptVersion: '2024.12.31.1200',
        // Force JSON response mode if API supports it
        response_format: { type: 'json_object' }
      });
      
      // Parse with validation
      const parsed = this.parseAndValidateJson(extractResponse.content, INITIATIVE_SCHEMA);
      
      if (parsed?.hasInitiative && parsed.confidence >= 0.3) {
        return this.enhanceInitiative(parsed.initiative, userMessage);
      }
      
      return null;
      
    } catch (error) {
      console.error('Error generating transferable initiative:', error);
      return null;
    }
  }
  
  /**
   * Build conversation summary for analysis
   */
  buildConversationSummary(userMessage, aiResponse, history) {
    let summary = `Latest exchange:\\n`;
    summary += `User: ${userMessage}\\n`;
    summary += `AI: ${aiResponse}\\n\\n`;
    
    if (history.length > 0) {
      summary += `Previous context:\\n`;
      history.slice(-4).forEach(msg => {
        const content = msg.content || '';
        summary += `${msg.role === 'user' ? 'User' : 'AI'}: ${content.substring(0, 200)}...\\n`;
      });
    }
    
    return summary;
  }
  
  /**
   * Parse and validate JSON response with schema
   */
  parseAndValidateJson(responseText, schema = null) {
    if (!responseText) return null;
    
    let parsed = null;
    
    // First attempt: direct parse (cleanest approach)
    try {
      parsed = JSON.parse(responseText);
    } catch (e) {
      // Second attempt: clean and parse
      try {
        // Remove common formatting issues
        const cleaned = responseText
          .replace(/^[^{]*/, '')  // Remove text before JSON
          .replace(/[^}]*$/, '')  // Remove text after JSON
          .replace(/```json\s*/g, '')
          .replace(/```/g, '')
          .trim();
        
        parsed = JSON.parse(cleaned);
      } catch (e2) {
        // Third attempt: extract JSON with regex
        const jsonMatch = responseText.match(/\{[\s\S]*\}/);;
        if (jsonMatch) {
          try {
            parsed = JSON.parse(jsonMatch[0]);
          } catch (e3) {
            console.error('Failed to parse JSON after all attempts:', e3);
            return null;
          }
        }
      }
    }
    
    // Validate against schema if provided
    if (parsed && schema) {
      return this.validateAgainstSchema(parsed, schema) ? parsed : null;
    }
    
    return parsed;
  }
  
  /**
   * Basic schema validation
   */
  validateAgainstSchema(obj, schema) {
    // Simple validation - can be enhanced with a proper validation library
    if (!obj || typeof obj !== 'object') return false;
    
    // Check for required fields
    if (schema.hasInitiative !== undefined && typeof obj.hasInitiative !== 'boolean') {
      console.warn('Invalid schema: missing or invalid hasInitiative');
      return false;
    }
    
    if (obj.hasInitiative && !obj.initiative) {
      console.warn('Invalid schema: hasInitiative is true but initiative is missing');
      return false;
    }
    
    if (obj.initiative && (!obj.initiative.title || !obj.initiative.objective)) {
      console.warn('Invalid schema: initiative missing required fields');
      return false;
    }
    
    return true;
  }
  
  /**
   * Enhance initiative with defaults and validation
   */
  enhanceInitiative(initiative, userMessage = '') {
    // Ensure all required fields
    const base = {
      title: initiative.title || 'Strategic Initiative',
      description: initiative.description || 'To be defined',
      objective: initiative.objective || 'Business objective',
      keyResults: initiative.keyResults || ['Define measurable outcome 1', 'Define measurable outcome 2'],
      strategicAlignment: initiative.strategicAlignment || 'Operational Excellence',
      priority: initiative.priority || 'medium',
      owner: initiative.owner || 'TBD',
      status: initiative.status || 'draft',
      // Pass through Impact Mapping terminology to Define form
      stakeholders: Array.isArray(initiative.stakeholders) ? initiative.stakeholders.filter(Boolean) : [],
      createdFrom: 'AI Chat',
      createdAt: new Date().toISOString()
    };

    // Augment stakeholders from latest user message if missing/empty
    try {
      const found = this.identifyStakeholdersFromText(String(userMessage || ''));
      if (found.length) {
        const set = new Set([...(base.stakeholders || []), ...found]);
        base.stakeholders = [...set].slice(0, 10);
      }
    } catch(_) {}
    return base;
  }

  /**
   * Identify potential stakeholders directly from user input.
   * Lightweight heuristic targeting end-user phrasing.
   */
  identifyStakeholdersFromText(text) {
    const s = String(text || '').toLowerCase();
    if (!s) return [];
    const hits = new Set();
    const add = (t) => { if (t && t.length <= 80) hits.add(t.replace(/\s+/g, ' ').trim()); };
    // Common personas and org roles
    const personas = [
      'customers', 'customer', 'clients', 'client', 'users', 'user', 'end users', 'end-user', 'end-users',
      'employees', 'staff', 'workforce', 'partners', 'vendors', 'suppliers',
      'sales', 'marketing', 'engineering', 'product managers', 'product management', 'pm', 'support', 'finance', 'hr'
    ];
    personas.forEach(p => { if (s.includes(p)) add(p.replace(/s$/, 's')); });
    // Patterns like "for X" or "among X"
    const forRe = /\bfor\s+([A-Za-z][A-Za-z\s&-]{1,40})/g;
    let m; while ((m = forRe.exec(s)) !== null) { add(m[1]); }
    // Quoted personas: "..."
    const qRe = /"([^"]{2,40})"/g; while ((m = qRe.exec(s)) !== null) { const v = m[1].toLowerCase(); if (personas.some(p=>v.includes(p))) add(v); }
    // Normalize capitalization
    return [...hits].map(t => t.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
  }
  
  /**
   * Add message to chat (FIXED: use AppState only, not both local and state)
   */
  addMessage(content, sender, transfer = null) {
    const message = {
      id: Date.now() + Math.random(), // More unique ID
      content,
      sender,
      transfer,
      timestamp: new Date().toISOString()
    };
    
    // FIXED: Only use AppState, not both local and global
    // The subscription will update our local messages array
    AppState.addChatMessage(message);
    
    // Update conversation depth (count user messages)
    if (sender === 'user') {
      this.conversationDepth++;
      console.log(`💬 Conversation depth: ${this.conversationDepth}`);
    }
    
    // Don't call render() here - let the subscription handle it
  }
  
  /**
   * Set typing state - uses RenderQueue
   */
  setTyping(typing) {
    this.isTyping = typing;
    // Use RenderQueue for efficient rendering
    renderQueue.markDirty(this, 3); // Lower priority for typing indicator
  }
  
  /**
   * Clear chat (FIXED: use AppState method)
   */
  clearChat() {
    // Do not persist on clear to avoid restoring long histories
    
    // Clear using AppState method (this will trigger subscription update)
    AppState.clearChat();
    
    // Reset local state
    this.conversationDepth = 0;
    this.conversationContext = {
      hasSpecificGoal: false,
      discussedMetrics: [],
      mentionedTimeframe: false,
      actionableItems: []
    };
    
    console.log('📨 Chat cleared');
  }
  
  /**
   * Transfer initiative to form - populate only, don't save
   */
  transferInitiative(initiative) {
    console.log('📝 Transferring initiative to form...', initiative);
    
    // Emit populate event for InitiativesModule
    EventBus.emit(AppEvents.INITIATIVE_POPULATE, initiative);
    
    // Also try direct population as backup
    this.populateInitiativeForm(initiative);
    
    // Show success message
    this.showToast('Initiative ready to edit in form', 'success');
  }
  
  /**
   * Populate form fields directly
   */
  populateInitiativeForm(initiative) {
    const fields = {
      'goal-title': initiative.title,
      'goal-description': initiative.description,
      'goal-objective': initiative.objective,
      'define-title': initiative.title,
      'define-description': initiative.description,
      'define-objective': initiative.objective
    };
    
    for (const [id, value] of Object.entries(fields)) {
      const field = document.getElementById(id);
      if (field) {
        field.value = value || '';
        field.dispatchEvent(new Event('input', { bubbles: true }));
        
        // Visual feedback
        field.style.transition = 'background-color 0.3s';
        field.style.backgroundColor = '#f0fdf4';
        setTimeout(() => {
          field.style.backgroundColor = '';
        }, 2000);
      }
    }
  }
  
  /**
   * Show toast notification
   */
  showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = `✅ ${message}`;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : '#ef4444'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => toast.remove(), 3000);
  }
  
  /**
   * Scroll to bottom of messages
   */
  scrollToBottom() {
    const messagesDiv = this.container?.querySelector('#chatMessages');
    if (messagesDiv) {
      messagesDiv.scrollTop = messagesDiv.scrollHeight;
    }
  }
  
  /**
   * Load chat history
   */
  loadChatHistory() {
    try {
      const saved = localStorage.getItem(Config.STORAGE_KEYS?.CHAT_HISTORY || 'chat_history');
      if (saved) {
        const history = JSON.parse(saved);
        // Load from AppState instead of direct assignment
        if (history.messages && history.messages.length > 0) {
          history.messages.forEach(msg => AppState.addChatMessage(msg));
        }
        this.conversationDepth = history.depth || 0;
        this.conversationContext = history.context || this.conversationContext;
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  }
  
  /**
   * Save chat history
   */
  saveChatHistory() {
    try {
      const history = {
        messages: this.messages,
        depth: this.conversationDepth,
        context: this.conversationContext,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(
        Config.STORAGE_KEYS?.CHAT_HISTORY || 'chat_history',
        JSON.stringify(history)
      );
    } catch (error) {
      console.error('Failed to save chat history:', error);
    }
  }
}

// Export for debugging
if (import.meta.env.DEV) {
  window.ChatModule = ChatModule;
  console.log('💬 ChatModule available at window.ChatModule');
}
